import TitleSection from '@/components/content/TitleSection'
import { PageLayout } from '@/components/layout/PageLayout'
import { HStack, Icon } from '@musinsa/mwms-ds'
import TabList, { TabProps } from '@/components/content/TabList'
import { borderRadius, colorSystem } from '@/styles/theme'
import Text from '@/components/content/Text'
import Button from '@/components/content/Button'
import { Divider } from '@/components/content/Divider'
import styled from 'styled-components'
import { useNavigate, useParams } from 'react-router-dom'
import { PATHS } from '@/utils/routes/paths'
import { AdjustmentModal, SettlementManagementDetailTable, SettlementSummary } from '@/pages/settlement/SettlementManagement/components'
import PromotionDiscountTab from '@/pages/settlement/SettlementManagement/components/PromotionDiscountTab'
import useModal from '@/hooks/useModal'
import { useMemo, useState } from 'react'
import { Select } from '@/components/content/Select'
import { useGetCountries } from '@/services/commonCode/useCommonCodeQueries'
import { useFeatureFlag } from '@/services/feature/useFeatureQueries'
import {
  useAddAdjustmentAmount,
  useGetPartnerSettlementAmountSummary,
  useGetSettlementStatisticsDetailSummary,
} from '@/services/settlement/settlementManagement/useSettlementManagementQueries'
import dayjs from 'dayjs'
import { DATE_FORMAT, REGEX } from '@/utils/consts'
import {
  AdjustmentAmountReq,
  CATEGORY_GLOBAL_SETTLEMENT_FLAG,
  ServiceTypeKey,
  SettlementAmountSummaryReq,
  SettlementStatisticsRes,
} from '@/services/settlement/settlementManagement/types'
import { useParsedQuery } from '@/hooks/useParsedQuery'
import { useSettlementStatisticsDetailQuery } from '@/pages/settlement/SettlementManagement/hooks/useSettlementStatisticsDetailQuery'
import MToast from '@/utils/modal/MToast'
import { useSettlementConfirm } from '@/pages/settlement/SettlementManagement/hooks/useSettlementConfirm'
import { SETTLEMENT_CLOSED_STATUS_MAP } from '@/pages/settlement/SettlementManagement/consts/consts'
import { useExportExcelSettlementStatisticsDetail } from '@/pages/settlement/SettlementManagement/hooks/useExportExcelSettlementStatisticsDetail'
import { GRID_SIZE } from '@/utils/grid/grid'
import { MonthDatePicker } from '../components/MonthDatePicker'
import { useMeasurementSettlementEnabled } from '@/pages/settlement/SettlementManagement/hooks/useMeasurementSettlementEnabled'
import { MenuStorage } from '@/utils/storage/MenuStorage'
import { useMenuOnSession } from '@/components/layout/MainMenuOnSessionProvider'
import { Utils } from '@/utils/Utils'
import { AxiosError } from 'axios'
import MAlert from '@/utils/modal/MAlert'

const SETTLEMENT_TAB_LABEL_MAP = {
  INVENTORY: '보관비',
  INBOUND: '입고비',
  OUTBOUND_TO_CUSTOMER: '고객출고비',
  OUTBOUND_TO_STORE: '매장출고비',
  RETURN_INBOUND: '반품비',
  MEASUREMENT: '실측비',
  ETC_ADJUSTMENT: '기타조정비',
} as const

// LOGISPRD-10354 — 프로모션 할인 탭은 서비스 탭과 데이터 원천이 달라(promotion summary API) ServiceTypeKey 밖의 키로 둔다
type DetailTabKey = ServiceTypeKey | 'PROMOTION'

function SettlementManagementDetail() {
  const { id } = useParams() as { id: string }
  const { parsedQs, replaceQuery } = useParsedQuery<SettlementAmountSummaryReq & { serviceType?: DetailTabKey }>()

  const navigate = useNavigate()
  const adjustmentModal = useModal()

  const { reload } = useMenuOnSession()

  const validYearMonth = REGEX.yearMonth.test(parsedQs.settlementMonth) ? parsedQs.settlementMonth : dayjs().format(DATE_FORMAT.dateMonth)
  const [requestedTab, setSelectedTab] = useState<DetailTabKey>((parsedQs.serviceType as DetailTabKey) || 'ALL')

  // LOGISPRD-16794 실측비 flag. OFF(조회 전·실패 포함)면 실측비 탭이 없고 serviceType=MEASUREMENT 딥링크는 전체 탭으로 보인다
  const isMeasurementEnabled = useMeasurementSettlementEnabled()
  const selectedTab: DetailTabKey = requestedTab === 'MEASUREMENT' && !isMeasurementEnabled ? 'ALL' : requestedTab

  // 프로모션 탭은 정산 통계 API 를 쓰지 않는다 — 통계 쿼리는 전체 탭 파라미터로 고정 (캐시 재사용, 오류 파라미터 전송 방지)
  const statsServiceType: ServiceTypeKey = selectedTab === 'PROMOTION' ? 'ALL' : selectedTab
  const [currentPage, setCurrentPage] = useState(0)

  // LP9182 국가 차원 노출 flag. OFF 면 국가 드롭다운 미노출 + country 미전송(레거시 동일)
  const { data: isCategoryGlobalSettlementEnabled } = useFeatureFlag(CATEGORY_GLOBAL_SETTLEMENT_FLAG)

  // LP9182 국가 필터 — URL(parsedQs) 주도. 전체 = undefined(미전송). 단가관리(#442) 통일, 중국은 BE 미지원이라 자동 미노출
  // useParsedQuery 가 qs.parse(comma:true) 라 손상/공유 URL(?country=KR,JP)이 배열로 파싱될 수 있어 단일 code 로 정규화(BE 는 단일 KR/JP 만 허용·다중은 400, SPEC §3)
  // flag OFF 면 공유/손상 URL 로 들어와도 country 미전송
  const rawCountry = parsedQs.country as string | string[] | undefined
  const normalizedCountry = Array.isArray(rawCountry) ? rawCountry[0] : rawCountry
  const country = isCategoryGlobalSettlementEnabled ? normalizedCountry : undefined
  // flag OFF 면 조회 자체 차단(no-op·실패 알럿 방지)
  const { data: countries } = useGetCountries(!!isCategoryGlobalSettlementEnabled)
  const countryOptions = useMemo(() => Utils.createSelectOptions(countries ?? [], 'name', 'code'), [countries])

  const {
    data: settlementStatisticsDetail,
    pageInfo,
    refetch: refetchSettlementStatisticsDetail,
  } = useSettlementStatisticsDetailQuery({
    partnerId: id,
    settlementMonth: validYearMonth,
    serviceType: statsServiceType,
    page: currentPage,
    size: GRID_SIZE.SETTLEMENT,
    country,
  })

  const { data: partnerSettlementAmounts, isLoading: isLoadingCutover } = useGetPartnerSettlementAmountSummary({
    partnerId: id,
    settlementMonth: validYearMonth,
    country,
  })

  const { data: settlementStatisticsDetailSummary } = useGetSettlementStatisticsDetailSummary({
    partnerId: id,
    settlementMonth: parsedQs.settlementMonth,
    serviceType: statsServiceType,
    country,
  })

  const { onExportData } = useExportExcelSettlementStatisticsDetail(statsServiceType)
  const adjustmentAmountMutation = useAddAdjustmentAmount()
  const { handleSettlementConfirm } = useSettlementConfirm()

  const settlementConfirmDisabled =
    partnerSettlementAmounts?.settlementClosedStatus === 'COMPLETED' ||
    !dayjs().isAfter(dayjs(partnerSettlementAmounts?.settlementEndDate), 'day')

  // 신규 정산 기준 발효 여부 — settlementMonth 가 cutoverMonth 이상이면 true
  // cutoverMonth (YearMonth, 예: "2026-06") — 이 월부터 신규 정산 기준 적용
  // 6월 정산부터 신규 기준
  //   - 5월 정산 (settlementMonth < cutoverMonth): 기존 (구매확정일자/클레임완료일자)
  //   - 6월 정산 (settlementMonth == cutoverMonth, 첫 전환월 6/1~6/25): 신규 (출고일자/입고일자)
  //   - 7월 정산 (settlementMonth > cutoverMonth, 6/26~7/25): 신규
  const cutoverMonthRaw = partnerSettlementAmounts?.cutoverMonth
  const cutoverMonth = cutoverMonthRaw ? dayjs(`${cutoverMonthRaw}-01`).startOf('month') : null
  const settlementMonthObj = dayjs(`${validYearMonth}-01`).startOf('month')
  // FFMPRO-816 cutover 헤더 flash 방어 — 로딩 중(특히 settlementStatisticsDetail 캐시 히트 시점)에는 신규 헤더(true) 기본 채택
  // 운영 안정화 후 가장 자주 보는 화면(6월 이후 정산) 에서의 flash 최소화가 목적
  // 부수효과: cutover 이전 시점 또는 옛 정산 조회 시에는 flash 가능 (시간 흐를수록 빈도 자연 감소)
  const cutoverApplied = isLoadingCutover ? true : !!cutoverMonth && !settlementMonthObj.isBefore(cutoverMonth)

  // 캘린더 최대 선택 가능 월
  // PRD 정책 고정: 정상 정산 주기 = 전월 26 ~ 당월 25 (cutoverDay = 26 고정)
  // 오늘이 cutoverMonth 이상이고 오늘의 day 가 26 이상이면 다음월 정산 데이터 시작 → 다음월 활성
  // (예: cutoverMonth=2026-06, 오늘=6/26 → 7월 정산 시작 → 7월 활성)
  // (예: cutoverMonth=2026-06, 오늘=6/11 → 6월 정산 누적 중 → 6월까지만 활성)
  const CUTOVER_DAY = 26
  const today = dayjs()
  const maxSelectableDate = ((): Date => {
    if (!cutoverMonth || today.startOf('month').isBefore(cutoverMonth)) {
      return today.toDate()
    }
    return today.date() >= CUTOVER_DAY
      ? today.add(1, 'month').endOf('month').toDate()
      : today.toDate()
  })()

  const handleTabChange = (tab: DetailTabKey) => {
    setSelectedTab(tab)
    setCurrentPage(0)
    replaceQuery({ ...parsedQs, serviceType: tab })
    MenuStorage.menuOnSession.setLinkSearch({ menuCode: 'bl03', query: { ...parsedQs, serviceType: tab } })

    reload()
  }

  const handleChangeDate = (value: string | Date | null) => {
    const settlementMonth = dayjs(value).format(DATE_FORMAT.dateMonth)
    replaceQuery({ ...parsedQs, settlementMonth })

    setCurrentPage(0)
    MenuStorage.menuOnSession.setLinkSearch({ menuCode: 'bl03', query: { ...parsedQs, settlementMonth } })

    reload()
  }

  // LP9182 — 국가 변경 시 모든 탭 쿼리에 country 반영. undefined = 전체(미전송). page 초기화
  const handleChangeCountry = (value?: string) => {
    replaceQuery({ ...parsedQs, country: value })
    setCurrentPage(0)
    MenuStorage.menuOnSession.setLinkSearch({ menuCode: 'bl03', query: { ...parsedQs, country: value } })

    reload()
  }

  const handleRegisterAdjustmentAmount = async (formValues: AdjustmentAmountReq) => {
    if (adjustmentAmountMutation.isPending) return
    try {
      await adjustmentAmountMutation.mutateAsync(formValues)
      MToast.show('기타조정금액이 등록되었습니다.')
      refetchSettlementStatisticsDetail()
    } catch (error) {
      MAlert.show(Utils.getApiErrMessage(error as AxiosError<ApiResponseV2>, '기타조정비 등록에 실패했습니다.'))
    }
  }

  const handleGoBack = () => {
    const query = { settlementMonth: parsedQs.settlementMonth }
    navigate(`${PATHS['bl.bl03']}${Utils.qsStringify(query)}`)
    MenuStorage.menuOnSession.setLinkSearch({ menuCode: 'bl03', link: PATHS['bl.bl03'] })
    reload()
  }
  const serviceTabList = [
    {
      key: 'ALL',
      label: '전체',
    },
    ...Object.entries(SETTLEMENT_TAB_LABEL_MAP)
      .filter(([key]) => isMeasurementEnabled || key !== 'MEASUREMENT')
      .map(([key, value]) => ({
        key,
        label: value,
      })),
  ].map((tab) => ({
    ...tab,
    content: (
      <SettlementManagementDetailTable<ServiceTypeKey>
        // TabList 가 활성 탭만 같은 자리에 그려 그리드 인스턴스가 재사용된다 — 탭별 컬럼 배치가 섞이지 않게 리마운트한다
        key={tab.key}
        currentTab={tab.key as ServiceTypeKey}
        summaryRowData={settlementStatisticsDetailSummary}
        rows={settlementStatisticsDetail}
        pageInfo={pageInfo}
        onChangePage={setCurrentPage}
        onExportData={() => onExportData({ partnerId: id, settlementMonth: validYearMonth, country })}
        cutoverApplied={cutoverApplied}
      />
    ),
  }))

  // LOGISPRD-10354 — 프로모션 할인 탭. 기간은 파트너 정산 요약의 정산 구간(전월 26 ~ 당월 25)
  const promotionTab = {
    key: 'PROMOTION',
    label: '프로모션',
    content: (
      <PromotionDiscountTab
        // 정산월·국가 변경 시 리마운트 — 이전 조건의 라인 페이지 번호가 새 조회에 살아남지 않게 한다
        key={`${validYearMonth}-${country ?? 'ALL'}`}
        partnerId={id}
        country={country}
        settlementDateFrom={
          partnerSettlementAmounts?.settlementStartDate
            ? dayjs(partnerSettlementAmounts.settlementStartDate).format(DATE_FORMAT.date)
            : undefined
        }
        settlementDateTo={
          partnerSettlementAmounts?.settlementEndDate
            ? dayjs(partnerSettlementAmounts.settlementEndDate).format(DATE_FORMAT.date)
            : undefined
        }
      />
    ),
  }

  // LOGISPRD-10354 — 프로모션 할인 탭은 서비스 탭 뒤에 항상 붙는다
  const tabList = [...serviceTabList, promotionTab]

  return (
    <>
      <PageLayout gap={16}>
        <NavigationHeader onClick={handleGoBack}>
          <Icon name="arrow-left-line" />
          <TitleSection variant="legacy" title={`${id} 정산내역`} />
        </NavigationHeader>
        <SettlementSummary
          title={
            <HStack gap={12} align="center">
              {dayjs(partnerSettlementAmounts?.settlementMonth).month() + 1}월 정산금액
              {partnerSettlementAmounts && (
                <>
                  <SettlementStatusTag status={partnerSettlementAmounts.settlementClosedStatus}>
                    {SETTLEMENT_CLOSED_STATUS_MAP[partnerSettlementAmounts.settlementClosedStatus]}
                  </SettlementStatusTag>
                  {!settlementConfirmDisabled && (
                    <Text kind="Body_14_regular" color="#F31110">
                      <Icon fill name="alert" size={16} style={{ marginRight: '4px' }} />
                      미확정 시, 파트너에게 정산되지 않습니다.
                    </Text>
                  )}
                </>
              )}
            </HStack>
          }
          settlementAmounts={partnerSettlementAmounts}
          promotionSavedAmount={partnerSettlementAmounts?.promotionSavedAmount}
          combinedShippingSavedAmount={partnerSettlementAmounts?.combinedShippingSavedAmount}
          description={
            <HStack align="center" style={{ color: colorSystem.text.common.tertiary }} gap={8}>
              <Text kind="Body_14_regular" color="inherit">
                {dayjs(partnerSettlementAmounts?.settlementMonth).month() + 2}월 청구예정
              </Text>
              <Divider variant="vertical" style={{ height: '10px' }} />
              {partnerSettlementAmounts && (
                <Text kind="Body_14_regular" color="inherit">
                  {dayjs(partnerSettlementAmounts.settlementStartDate).format(DATE_FORMAT.date)}
                  &nbsp;~&nbsp;
                  {dayjs(partnerSettlementAmounts.settlementEndDate).format(DATE_FORMAT.date)}
                </Text>
              )}
            </HStack>
          }
          rightArea={
            <HStack gap={8} align="center" style={{ height: '36px' }}>
              <Button
                fit
                kind="primary_light"
                size="md"
                content="마감 확정하기"
                disabled={settlementConfirmDisabled}
                onClick={() => handleSettlementConfirm({ settlementMonth: validYearMonth, partnerId: id })}
              />
              <Button fit kind="secondary" size="md" content="기타조정비 등록" onClick={adjustmentModal.onOpenModal} />
              <Divider variant="vertical" style={{ height: '16px' }} />
              {/* LP9182 국가 드롭다운(전체/한국/일본) — 정산월 옆. 전체 = placeholder + onClear. flag OFF 면 미노출 */}
              {isCategoryGlobalSettlementEnabled && (
                <div style={{ width: 140 }}>
                  <Select
                    value={country}
                    onValueChange={handleChangeCountry}
                    onClear={() => handleChangeCountry(undefined)}
                    options={countryOptions}
                    placeholder="전체"
                  />
                </div>
              )}
              <MonthDatePicker size="lg" value={validYearMonth} onChange={handleChangeDate} inputWidth={148} maxDate={maxSelectableDate} />
            </HStack>
          }
        />
        <TabList list={tabList as TabProps<DetailTabKey>[]} activeKey={selectedTab} onTabChange={handleTabChange} />
      </PageLayout>
      {adjustmentModal.open && (
        <AdjustmentModal
          settlementMonth={dayjs(validYearMonth).format(DATE_FORMAT.dateMonth)}
          partnerId={id}
          onConfirm={handleRegisterAdjustmentAmount}
          onClose={adjustmentModal.onCloseModal}
        />
      )}
    </>
  )
}
const SettlementStatusTag = styled.div<{ status: SettlementStatisticsRes['settlementClosedStatus'] }>`
  height: 28px;
  padding: 5px 8px;
  background-color: ${({ status }) => (status === 'COMPLETED' ? colorSystem.bg.common.secondary_hover : 'rgba(243, 17, 16, 0.1)')};
  color: ${({ status }) => (status === 'COMPLETED' ? colorSystem.text.common.disabled_inverse : '#F31110')};
  ${borderRadius.sm};
  font-size: 12px;
  line-height: 20px;
  font-weight: 400;
`

const NavigationHeader = styled('button')`
  display: flex;
  align-items: center;
  gap: 8px;
  width: fit-content;
`
export default SettlementManagementDetail
